import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Star, MessagesSquare, ImagePlus, Loader2, RefreshCw, Activity, Settings, DoorOpen, UserPlus } from 'lucide-react';
import activityService from '../../services/activityService';
import communityService from '../../services/communityService';
import ThumbnailImage from '../ThumbnailImage';
import { t } from '../../i18n';
import { ActivityItem, ActivityType, activitySentenceKey, actorLabel, mergeActivityPages, relativeTimeParts, starsText, targetLabel } from '../../utils/activityStream';

/**
 * Aktivitäten-Tab auf der Startseite (Marcel 2026-10-08): jüngste Community-Aktivitäten
 * (Kommentare, Bewertungen, Setup-Kommentare, Forum, Beiträge) als flache Zeilen mit Trennlinien –
 * Avatar links, Satz (Akteur + Ziel fett), Textauszug, relative Zeit; Klick → Ziel-Route.
 * Lazy: wird erst beim ersten Öffnen des Tabs gemountet (HomePage).
 */
const ACCENT = '#3fced1';
const PAGE = 30;

const TYPE_ICON: Record<ActivityType, typeof MessageCircle> = {
  comment: MessageCircle, rating: Star, setupComment: MessageCircle, forumTopic: MessagesSquare, forumPost: MessagesSquare, post: ImagePlus,
  setup: Settings, dartRoom: DoorOpen, follow: UserPlus,
};

const relLabel = (createdAt: number): string => {
  const { key, value } = relativeTimeParts(createdAt);
  return t(`web.component.home-activity-list.${key}`, { count: value });
};

const Sentence: React.FC<{ item: ActivityItem }> = ({ item }) => {
  const actor = actorLabel(item.actor, t('web.component.home-activity-list.jemand'));
  const target = targetLabel(item.target, t('web.component.home-activity-list.einen-eintrag'));
  // Satz mit Platzhaltern [actor]/[target]/[stars] → fette Teile
  const tpl = t(activitySentenceKey(item.type, item.reply), { actor: '[actor]', target: '[target]', stars: '[stars]' });
  const parts = tpl.split(/(\[actor\]|\[target\]|\[stars\])/g).filter(Boolean);
  return (
    <span className="text-[13.5px] leading-snug text-zinc-300">
      {parts.map((p, i) => {
        if (p === '[actor]') return <span key={i} className="font-semibold text-white">{actor}</span>;
        if (p === '[target]') return <span key={i} className="font-semibold text-white">{target}</span>;
        if (p === '[stars]') return <span key={i} className="font-semibold tabular-nums" style={{ color: ACCENT }} data-testid="activity-stars">{starsText(item.stars)}</span>;
        return <React.Fragment key={i}>{p}</React.Fragment>;
      })}
    </span>
  );
};

const Row: React.FC<{ item: ActivityItem; onOpen: (route: string) => void }> = ({ item, onOpen }) => {
  const Icon = TYPE_ICON[item.type] ?? Activity;
  // Avatar: 100er-Thumbnail mit Fallback aufs Original (ThumbnailImage), bei Ladefehler Initiale
  const avatar = item.actor.image ? communityService.buildImageUrl(item.actor.image) : null;
  const [avatarFailed, setAvatarFailed] = useState(false);
  const initial = (item.actor.name ?? '?').slice(0, 1).toUpperCase();
  const clickable = !!item.target.route;
  return (
    <li className="border-b border-zinc-800 last:border-b-0" data-testid="activity-row" data-type={item.type}>
      <button
        type="button"
        onClick={() => item.target.route && onOpen(item.target.route)}
        disabled={!clickable}
        className="flex w-full items-start gap-3 px-1 py-3 text-left transition-colors hover:bg-zinc-900/60 disabled:cursor-default"
      >
        <span className="relative mt-0.5 h-9 w-9 shrink-0">
          {avatar && !avatarFailed ? (
            <ThumbnailImage src={avatar} thumbSize={100} alt="" loading="lazy" onError={() => setAvatarFailed(true)} className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-800 text-[12px] font-bold text-zinc-400">{initial}</span>
          )}
          <span className="absolute -bottom-1 -right-1 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-black p-0.5" aria-hidden="true">
            <Icon size={12} style={{ color: ACCENT }} />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-3">
            <Sentence item={item} />
            <span className="shrink-0 pt-0.5 text-[11px] text-zinc-500" data-testid="activity-time">{relLabel(item.createdAt)}</span>
          </span>
          {item.text && <span className="mt-1 line-clamp-2 block text-[12.5px] leading-snug text-zinc-500">{item.text}</span>}
        </span>
      </button>
    </li>
  );
};

const HomeActivityList: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setFailed(false);
    try {
      const page = await activityService.getRecent({ limit: PAGE });
      setItems(page.items); setNextBefore(page.nextBefore);
    } catch { setFailed(true); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await activityService.getRecent({ limit: PAGE, before: nextBefore });
      setItems((prev) => mergeActivityPages(prev, page.items)); setNextBefore(page.nextBefore);
    } catch { /* Hinweis unten bleibt, erneut klicken */ } finally { setLoadingMore(false); }
  };

  return (
    <section aria-label={t('web.component.home-activity-list.aktivitaeten')} data-testid="home-activity">
      <div className="mb-2 flex items-center justify-between">
        <div>
          <h2 className="section-heading text-base font-bold text-white">{t('web.component.home-activity-list.aktivitaeten')}</h2>
          <p className="mt-0.5 text-xs text-gray-500">{t('web.component.home-activity-list.untertitel')}</p>
        </div>
        <button type="button" onClick={load} disabled={loading} aria-label={t('web.component.home-activity-list.aktualisieren')} title={t('web.component.home-activity-list.aktualisieren')} className="flex h-8 w-8 items-center justify-center rounded-full text-zinc-400 transition hover:bg-zinc-900 hover:text-white disabled:opacity-40" data-testid="activity-reload">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-zinc-500" data-testid="activity-loading"><Loader2 size={16} className="animate-spin" /> {t('web.component.home-activity-list.laedt')}</div>
      ) : failed ? (
        <div className="py-8 text-center text-sm text-zinc-500" data-testid="activity-error">
          <p>{t('web.component.home-activity-list.konnte-nicht-geladen-werden')}</p>
          <button type="button" onClick={load} className="mt-2 text-sm font-semibold" style={{ color: ACCENT }}>{t('web.component.home-activity-list.erneut-versuchen')}</button>
        </div>
      ) : items.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500" data-testid="activity-empty">{t('web.component.home-activity-list.noch-keine-aktivitaeten')}</p>
      ) : (
        <>
          <ul className="divide-zinc-800">
            {items.map((item) => <Row key={item.id} item={item} onOpen={(r) => navigate(r)} />)}
          </ul>
          {nextBefore && (
            <div className="flex justify-center pt-4">
              <button type="button" onClick={loadMore} disabled={loadingMore} className="rounded-full border border-zinc-700 px-4 py-1.5 text-[13px] font-semibold text-zinc-200 transition hover:border-[#3fced1]/60 disabled:opacity-50" data-testid="activity-more">
                {loadingMore ? t('web.component.home-activity-list.laedt') : t('web.component.home-activity-list.mehr-laden')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default HomeActivityList;
